import React from 'react';
import { useTheme } from '../../../../../hooks/useTheme';
import { AppTheme } from '../../../../../theme';
import { Box } from '../../../../box';
import { Typography } from '../../../../typography';
import { RangeInputEdge } from './components/edge';
import { RangeInputRoot } from './components/root';
import { Slider } from './components/slider';
import {
  DefaultSetOfRangeTokens,
  RANGE_INPUT_DEFAULT_MAX,
  RANGE_INPUT_DEFAULT_MIN,
  RANGE_INPUT_DEFAULT_STEP,
} from './const';
import {
  RangeInputProps,
  RangeInputStateProps,
  RangeInputValue,
  RangeInputValueItemRenderFn,
} from './model';

type ValueRowProps = {
  isMinimal: boolean;
  isSingle: boolean;
  ownerState: Required<RangeInputStateProps>;
  renderValueItem: RangeInputValueItemRenderFn;
  theme: AppTheme;
  value: RangeInputValue;
};

function RangeInputValueRow({ isMinimal, isSingle, ownerState, renderValueItem, theme, value }: ValueRowProps) {
  if (isSingle) {
    return <RangeInputEdge ownerState={ownerState}>{renderValueItem(Number(value), { index: 0 })}</RangeInputEdge>;
  }

  if (isMinimal) {
    return (
      <>
        <RangeInputEdge ownerState={ownerState}>{renderValueItem(value[0], { index: 0 })}</RangeInputEdge>
        <RangeInputEdge ownerState={ownerState}>{renderValueItem(value[1], { index: 1 })}</RangeInputEdge>
      </>
    );
  }

  return (
    <>
      <RangeInputEdge ownerState={ownerState} sx={{ borderRight: `1px solid ${theme.palette['gray light']}` }}>
        {renderValueItem(value[0], { index: 0 })}
      </RangeInputEdge>
      <RangeInputEdge ownerState={ownerState} sx={{ pl: 2 }}>
        {renderValueItem(value[1], { index: 1 })}
      </RangeInputEdge>
    </>
  );
}

export const RangeInput = React.forwardRef<HTMLDivElement, RangeInputProps>((props, ref) => {
  const theme = useTheme();
  const {
    error = false,
    disabled = false,
    sx,
    value: _value,
    onChange,
    step = RANGE_INPUT_DEFAULT_STEP,
    min = RANGE_INPUT_DEFAULT_MIN,
    max = RANGE_INPUT_DEFAULT_MAX,
    renderValueItem = (v) => v,
    single = false,
    disableValueInput = false,
    showBoundaryValues = false,
    sliderSize = 'small',
    size = 'normal',
    variant: variantProp = 'contained',
    rangeSliderStyle = false,
    marks: marksProp,
    valueLabelDisplay: valueLabelDisplayProp,
    disableSwap: disableSwapProp,
    ...otherProps
  } = props;
  const [localValue, setLocalValue] = React.useState<RangeInputValue | null>(null);

  const variant = rangeSliderStyle ? 'minimal' : variantProp;
  const isMinimal = variant === 'minimal';

  const isSingle = _value && Array.isArray(_value) ? false : single;

  const value =
    localValue !== null
      ? localValue
      : (([undefined, null].includes(_value as undefined) ? (isSingle ? 0 : [0, 0]) : _value) as RangeInputValue);

  const marks = marksProp ?? !isMinimal;
  const valueLabelDisplay = valueLabelDisplayProp ?? (isMinimal ? 'off' : 'auto');
  const disableSwap = disableSwapProp ?? isMinimal;

  const ownerState: Required<RangeInputStateProps> = {
    disableValueInput,
    error,
    disabled,
    sliderSize,
    value,
    size,
    variant,
  };

  const rootSx = {
    ...(variant === 'contained' ? DefaultSetOfRangeTokens : {}),
    ...sx,
  };

  const handleSliderChange = React.useCallback(
    (_event: Event, newValue: number | number[]) => {
      setLocalValue(newValue as RangeInputValue);
    },
    [],
  );

  const handleSliderCommit = React.useCallback(
    (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
      onChange?.(newValue as RangeInputValue);
      setLocalValue(null);
    },
    [onChange],
  );

  return (
    <RangeInputRoot ownerState={ownerState} ref={ref} sx={rootSx} {...otherProps}>
      {!disableValueInput && (
        <Box
          component="div"
          sx={{
            display: 'flex',
            ...(isMinimal ? { justifyContent: 'space-between' } : {}),
          }}
        >
          <RangeInputValueRow
            isMinimal={isMinimal}
            isSingle={isSingle}
            ownerState={ownerState}
            renderValueItem={renderValueItem}
            theme={theme}
            value={value}
          />
        </Box>
      )}

      <Slider
        disableSwap={disableSwap}
        disabled={disabled}
        marks={marks}
        max={max}
        min={min}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderCommit}
        ownerState={{ variant, sliderSize }}
        step={step}
        value={value}
        valueLabelDisplay={valueLabelDisplay}
      />
      {showBoundaryValues && (
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Typography sx={{ typography: { xs: 'button', md: 'h5' } }}>{min}</Typography>
          <Typography sx={{ typography: { xs: 'button', md: 'h5' } }}>{max}</Typography>
        </Box>
      )}
    </RangeInputRoot>
  );
});

export { CssTokensOfRange, DefaultSetOfRangeTokens, LargeSetOfRangeTokens } from './const';
